import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigurationService, PathsService, UserManagementService } from '@draeger-academy/common';
import { AuthenticationService } from '@draeger-academy/common/lib/services/authentication/authentication.service';
import { CartService } from '@draeger-academy/common/lib/services/cart/cart.service';
import { OrderService } from '@draeger-academy/common/lib/services/order/order.service';
import { FormsUtils } from '@draeger-academy/presentation/lib/modules/form-builder';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router/lib/localize-router.service';
import { Order } from 'models/order';
import { User } from 'models/user';
import { Observable, Subject, BehaviorSubject, Subscription } from 'rxjs';
import { first, take } from 'rxjs/operators';
import { GenericModalService } from '../../../generic-modal';

@Component({
    selector: 'da-login-popup',
    templateUrl: './login-popup.component.html',
    styleUrls: ['./login-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LoginPopupComponent implements OnInit {
    public activeStepLogin = 1;
    public loginForm: UntypedFormGroup;
    loading = false;
    submitted = false;
    remember = false;
    showError = false;
    @Input() targetUrl: string;
    @Input() isCheckout: string;
    @Output()
    checkoutProceed: EventEmitter<string> = new EventEmitter<string>();

    @Output()
    closePopup: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Output()
    userLoggedIn: EventEmitter<boolean> = new EventEmitter<boolean>();

    hasFocusUsername = false;
    hasFocusPassword = false;
    user: User = {
        id: 0,
        username: '',
        password: '',
        token: ''
    };
    private _$showRememberMe: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$showForgotPassword: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$showError: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public linkForgotPassword: string;

    forgotPasswordForm: UntypedFormGroup;
    loadingForgotPass = false;
    submittedForgotPass = false;
    message = false;
    showMessage = false;
    public hasFocus = false;
    public success = true;
    // convenience getter for easy access to form fields
    linkLoginForm: string;
    username2: string;

    private _$loaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public get loaded$(): Observable<{}> {
        return this._$loaded.asObservable();
    }

    private _$activeStepLogin2: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    public get activeStepLogin2$(): Observable<{}> {
        return this._$activeStepLogin2.asObservable();
    }

    private _$activeStepLogin3: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    public get activeStepLogin3$(): Observable<{}> {
        return this._$activeStepLogin3.asObservable();
    }

    private _$showErrorMessageBackEnd: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public get showErrorMessageBackEnd$(): Observable<{}> {
        return this._$showErrorMessageBackEnd.asObservable();
    }

    private _$hasLoginInfoSpecialAccess: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public get hasLoginInfoSpecialAccess$(): Observable<{}> {
        return this._$hasLoginInfoSpecialAccess.asObservable();
    }

    constructor(
        private formBuilder: UntypedFormBuilder,
        private router: Router,
        private authenticationService: AuthenticationService,
        private _configurationService: ConfigurationService,
        private _activatedRoute: ActivatedRoute,
        private _pathsService: PathsService
    ) {}

    public get showRememberMe$(): Observable<boolean> {
        return this._$showRememberMe.asObservable();
    }

    public get showForgotPassword$(): Observable<boolean> {
        return this._$showForgotPassword.asObservable();
    }

    public get showError$(): Observable<boolean> {
        return this._$showError.asObservable();
    }

    // convenience getter for easy access to form fields
    get f() {
        return this.loginForm.controls;
    }

    get f2() {
        return this.forgotPasswordForm.controls;
    }

    ngOnInit() {
        this.linkForgotPassword = this._pathsService.getForgotPasswordLink();
        this._$showForgotPassword.next(this._configurationService.hasFeature('ForgotPassword'));
        this._$showRememberMe.next(this._configurationService.hasFeature('RememberMe'));
        this.checkFeaturesOnPortals();

        this.loginForm = this.formBuilder.group({
            username: ['', [Validators.required, Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,10}$')]],
            password: ['', Validators.required],
            remember: [false]
        });

        this.linkLoginForm = this._pathsService.getAccountLink();
        this.success = false;
        this.forgotPasswordForm = this.formBuilder.group({
            username2: ['', [Validators.required, Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,10}$')]]
        });
    }

    public hasValue(fieldname) {
        const _input = fieldname;
        return _input.value ? FormsUtils.trim(_input.value.toString()) !== '' : false;
    }

    onSubmit() {
        this.submitted = true;
        this._configurationService.updateloggedInByPopup(true);

        // stop here if form is invalid
        if (this.loginForm.invalid) {
            return;
        }

        this.loading = true;
        this.authenticationService
            .login(this.f.username.value, this.f.password.value, this.f.remember.value)
            .pipe(first())
            .subscribe(
                (data) => {
                    if (data.token) {
                        this.closePopup.emit(true);
                        this.userLoggedIn.emit(true);
                    } else {
                        this.showError = true;
                        this._$showError.next(true);
                    }

                    this.loading = false;
                },
                (error) => {
                    this.showError = true;
                    this._$showError.next(true);
                    console.log(error);
                    this.loading = false;
                }
            );
    }

    onSubmitForgotPassword() {
        this.submitted = true;
        this.username2 = this.f2.username2.value;

        if (this.forgotPasswordForm.invalid) {
            return;
        }

        this._$loaded.next(false);
        this.authenticationService
            .forgotPassword(this.f2.username2.value)
            .pipe(first())
            .subscribe(
                (data) => {
                    this.activeStepLogin = 3;
                    this._$activeStepLogin3.next(3);
                    this._$activeStepLogin2.next(0);
                },
                (error) => {
                    this.activeStepLogin = 3;
                    this._$activeStepLogin3.next(3);
                    this._$activeStepLogin2.next(0);
                    // console.log(error);
                    // this.showMessage = true;
                    // this.success = false;
                    // this._$showErrorMessageBackEnd.next(true);
                }
            );
    }

    onCheckboxChange(remember) {
        this.remember = remember;
    }

    cancel() {
        this.closePopup.emit(true);
    }

    public forgotPassword() {
        this.activeStepLogin = 2;
        this._$activeStepLogin2.next(2);
        this._$activeStepLogin3.next(0);
    }

    checkFeaturesOnPortals() {
        const isPortal = this._configurationService.isPartnerPage();

        if (isPortal) {
            this._configurationService.getConfigPartnerByPortal().then((values) => {
                if (values) {
                    const tempFeatures = values.filter((setting) => setting.key.toLowerCase() === 'features');
                    let hasForgotPassword;
                    if (tempFeatures && tempFeatures[0] && tempFeatures[0].value && tempFeatures[0].value.length > 0) {
                        hasForgotPassword = tempFeatures[0].value.filter((setting) => setting.name === 'ForgotPassword');
                    }
                    if (hasForgotPassword && hasForgotPassword[0] && hasForgotPassword[0].enabled) {
                        this._$showForgotPassword.next(true);
                    }

                    let hasRememberMe;
                    if (tempFeatures && tempFeatures[0] && tempFeatures[0].value && tempFeatures[0].value.length > 0) {
                        hasRememberMe = tempFeatures[0].value.filter((setting) => setting.name === 'RememberMe');
                    }
                    if (hasRememberMe && hasRememberMe[0] && hasRememberMe[0].enabled) {
                        this._$showRememberMe.next(true);
                    }

                    let hasLoginInfoSpecialAccess;
                    if (tempFeatures && tempFeatures[0] && tempFeatures[0].value && tempFeatures[0].value.length > 0) {
                        hasLoginInfoSpecialAccess = tempFeatures[0].value.filter((setting) => setting.name === 'LoginInfoSpecialAccess');
                    }
                    if (hasLoginInfoSpecialAccess && hasLoginInfoSpecialAccess[0] && hasLoginInfoSpecialAccess[0].enabled) {
                        this._$hasLoginInfoSpecialAccess.next(true);
                    }
                }
            });
        }
    }
}
